import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Renders the recently viewed (previously seen) products.
 *
 * Reads the product ids saved in localStorage by `RecentlyViewed`, then fetches
 * the matching product cards through the Search Section Rendering API so the
 * cards are rendered server-side using the theme's `_product-card` block.
 */
class RecentlyViewedProducts extends HTMLElement {
  /** @type {boolean} */
  #loaded = false;

  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }

  /**
   * Loads and renders the recently viewed products.
   */
  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { sectionId, productId, searchUrl } = this.dataset;
    const maxProducts = parseInt(this.dataset.maxProducts ?? '', 10) || 4;

    if (!sectionId || !searchUrl) {
      this.#hide();
      return;
    }

    let ids = RecentlyViewed.getProducts().map((id) => String(id));

    // Don't show the product the visitor is currently looking at.
    if (productId) {
      ids = ids.filter((id) => id !== String(productId));
    }

    ids = ids.slice(0, maxProducts);

    if (ids.length === 0) {
      this.#hide();
      return;
    }

    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const html = await response.text();
      this.#render(html, ids);
    } catch (error) {
      console.error('Recently viewed error:', error instanceof Error ? error.message : error);
      this.#hide();
    }
  }

  /**
   * Injects the fetched product cards, restoring the recency order.
   * @param {string} html - The Section Rendering API response.
   * @param {string[]} ids - The product ids in recency order (most recent first).
   */
  #render(html, ids) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newResults = fragment.querySelector('[data-recently-viewed-results]');
    const liveResults = this.querySelector('[data-recently-viewed-results]');

    if (!newResults || !liveResults) {
      this.#hide();
      return;
    }

    const list = newResults.querySelector('.resource-list');
    const items = list ? Array.from(list.querySelectorAll(':scope > .resource-list__item')) : [];

    if (items.length === 0) {
      this.#hide();
      return;
    }

    // Search returns results by relevance, so re-sort them to match the order
    // in which the products were actually viewed (most recent first).
    if (list) {
      const ordered = ids
        .map((id) => items.find((item) => item instanceof HTMLElement && item.dataset.productId === id))
        .filter((item) => item != null);
      const remaining = items.filter((item) => !ordered.includes(item));
      list.replaceChildren(...ordered, ...remaining);
    }

    liveResults.innerHTML = newResults.innerHTML;
  }

  /**
   * Hides the section when there's nothing to show.
   */
  #hide() {
    this.hidden = true;
    this.style.display = 'none';
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewedProducts);
}
